/*
  Space Ritual — the guided finder.

  Everything it can show is already in the document; this only decides which
  step is visible and which outcome matches the answers. No fetching, no
  templating, so the product link and price are whatever Shopify rendered.

  Opens from the floating launcher, from any link pointing at #space-ritual,
  and from that hash on load.
*/
(function () {
  var HASH = '#space-ritual';
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Long enough that the chosen answer is seen to register before the step
     changes under it, short enough that four questions still move. */
  var SETTLE = 260;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  class AlepuRitual extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-ritual-modal]');
      this.panel = this.querySelector('[data-ritual-panel]');
      this.track = this.querySelector('[data-ritual-track]');
      if (!this.modal || !this.track) return;

      this.steps = Array.prototype.slice.call(this.track.querySelectorAll('[data-ritual-step]'));
      this.questions = this.steps.filter(function (s) {
        return s.dataset.ritualStep === 'question';
      });
      this.resultStep = this.track.querySelector('[data-ritual-step="result"]');
      this.results = Array.prototype.slice.call(this.track.querySelectorAll('[data-ritual-result]'));
      this.progress = this.querySelector('[data-ritual-progress]');
      this.backButton = this.querySelector('[data-ritual-back]');

      this.answers = {};
      this.index = 0;
      this.opener = null;

      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-ritual-open]').forEach((el) => {
        el.addEventListener('click', (event) => this.open(event.currentTarget));
      });

      this.querySelectorAll('[data-ritual-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      var start = this.querySelector('[data-ritual-start]');
      if (start) start.addEventListener('click', () => this.goTo(1));

      this.querySelectorAll('[data-ritual-option]').forEach((option) => {
        option.addEventListener('click', (event) => this.choose(event.currentTarget));
      });

      if (this.backButton) this.backButton.addEventListener('click', () => this.goTo(this.index - 1));

      this.querySelectorAll('[data-ritual-restart]').forEach((el) => {
        el.addEventListener('click', () => this.restart());
      });

      /* Any link to the finder opens it in place rather than navigating. */
      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);

      if (window.location.hash === HASH) this.open(null);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.body.classList.remove('alepu-ritual-locked');
    }

    onDocumentClick(event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href !== HASH && href.slice(-HASH.length) !== HASH) return;
      event.preventDefault();
      this.open(link);
    }

    /* ------------------------------------------------------------- open */

    open(opener) {
      if (!this.modal.hidden) return;
      this.opener = opener;
      this.modal.hidden = false;
      void this.modal.offsetHeight;
      this.classList.add('is-open');
      document.body.classList.add('alepu-ritual-locked');
      document.addEventListener('keydown', this.onKeydown);

      this.render();
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, this.panel.querySelector('button, a'));
      } else {
        this.panel.focus();
      }
    }

    close() {
      if (this.modal.hidden) return;
      this.classList.remove('is-open');
      document.body.classList.remove('alepu-ritual-locked');
      document.removeEventListener('keydown', this.onKeydown);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener || undefined);

      var modal = this.modal;
      setTimeout(function () {
        modal.hidden = true;
      }, 280);
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    /* ------------------------------------------------------------- steps */

    /* 0 is the opening panel, 1..n the questions, n+1 the outcome. */
    goTo(index) {
      this.index = Math.max(0, Math.min(index, this.questions.length + 1));
      if (this.index === this.questions.length + 1) this.resolve();
      this.render();

      var active = this.activeStep();
      if (!active) return;
      var focusable = active.querySelector('button, a');
      if (!focusable) return;

      /* preventScroll keeps the entrance animation from being yanked to the
         top of the track by the focus call landing mid-transform. */
      try {
        focusable.focus({ preventScroll: true });
      } catch (e) {
        focusable.focus();
      }
    }

    activeStep() {
      if (this.index === 0) return this.steps[0];
      if (this.index === this.questions.length + 1) return this.resultStep;
      return this.questions[this.index - 1];
    }

    render() {
      var active = this.activeStep();
      this.steps.forEach(function (step) {
        var on = step === active;
        step.hidden = !on;
        step.classList.toggle('is-active', on);
      });

      if (this.progress) {
        if (this.index > 0 && this.index <= this.questions.length) {
          this.progress.textContent = pad(this.index) + ' / ' + pad(this.questions.length);
        } else {
          this.progress.textContent = '';
        }
      }

      if (this.backButton) this.backButton.hidden = this.index === 0;
      this.track.scrollTop = 0;

      /* Retrigger the entrance. Removing the class, reading a layout value to
         commit it, then re-adding is the only way to replay a CSS animation on
         an element that already carries it. */
      if (active && !REDUCE.matches) {
        active.classList.remove('is-entering');
        void active.offsetWidth;
        active.classList.add('is-entering');
      }
    }

    choose(option) {
      var step = option.closest('[data-ritual-step]');
      var key = step.dataset.ritualKey;

      step.querySelectorAll('[data-ritual-option]').forEach(function (el) {
        el.classList.toggle('is-chosen', el === option);
      });

      this.answers[key] = {
        value: option.dataset.ritualValue,
        label: option.dataset.ritualLabel,
        question: step.querySelector('.alepu-ritual__heading').textContent,
      };

      if (REDUCE.matches) {
        this.goTo(this.index + 1);
        return;
      }

      /* Guarded, so a double tap cannot queue two advances. */
      if (this.settling) return;
      this.settling = true;
      setTimeout(() => {
        this.settling = false;
        this.goTo(this.index + 1);
      }, SETTLE);
    }

    restart() {
      this.answers = {};
      this.querySelectorAll('[data-ritual-option].is-chosen').forEach(function (el) {
        el.classList.remove('is-chosen');
      });
      this.goTo(0);
    }

    /* ----------------------------------------------------------- outcome */

    matches(list, answer) {
      if (!list) return true;
      if (!answer) return false;
      return (
        list
          .split(',')
          .map(function (part) {
            return part.trim();
          })
          .filter(Boolean)
          .indexOf(answer.value) !== -1
      );
    }

    resolve() {
      var self = this;
      var chosen = this.results.find(function (result) {
        return (
          self.matches(result.dataset.matchFeel, self.answers.feel) &&
          self.matches(result.dataset.matchScent, self.answers.scent)
        );
      });

      /* A result with both match fields empty is the catch-all. If the merchant
         has narrowed every one of them, fall back to the last rather than
         showing an empty panel. */
      if (!chosen) chosen = this.results[this.results.length - 1];

      this.results.forEach(function (result) {
        result.hidden = result !== chosen;
      });

      if (chosen) this.fillAnswers(chosen);
    }

    fillAnswers(result) {
      var list = result.querySelector('[data-ritual-answers]');
      if (!list) return;
      list.textContent = '';

      var self = this;
      this.questions.forEach(function (step) {
        var answer = self.answers[step.dataset.ritualKey];
        if (!answer) return;

        var item = document.createElement('li');
        var term = document.createElement('span');
        term.className = 'alepu-ritual__answer-q';
        term.textContent = answer.question;
        var value = document.createElement('span');
        value.className = 'alepu-ritual__answer-a';
        value.textContent = answer.label;
        item.appendChild(term);
        item.appendChild(value);
        list.appendChild(item);
      });
    }
  }

  if (!customElements.get('alepu-ritual')) customElements.define('alepu-ritual', AlepuRitual);
})();
